import { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Flex from 'legacyfrontend/components/Flex';
import formatSecondsToHours from 'legacyfrontend/utils/formatSecondsToHours';
import { formatDate, formatTime } from 'legacyfrontend/utils/dateUtils';
import { useColors } from 'legacyfrontend/hooks/useColors';
import TooltipScale from '../FlightCard/TooltipScale';
import TooltipLuggage from '../FlightCard/TooltipLuggage';
import FlightSingleDetailDialog from './FlightSingleDetailDialog';

const FlightSingleInfoRow = ({ flight, kind }) => {
  const { coral } = useColors();
  const [scales, setScales] = useState([]);

  const flightData = useMemo(() => {
    const { segments } = flight;
    const firstSegment = segments[0];
    const lastSegment = segments[segments.length - 1];

    return {
      departureDate: firstSegment.departure_date_time,
      arrivalDate: lastSegment.arrival_date_time,
      departureCity: firstSegment.origin_city_name,
      arrivalCity: lastSegment.destination_city_name,
      hasScales: segments.length > 1,
      originId: firstSegment.origin_iata,
      destinationId: lastSegment.destination_iata,
      duration: flight.duration,
    };
  }, [flight]);

  const airlineLogos = useMemo(() => {
    return [...new Set(flight.segments.map((segment) => segment.logoCompany))].filter(Boolean);
  }, [flight]);

  useEffect(() => {
    const calculateScales = () => {
      const { segments } = flight;
      if (segments.length <= 1) {
        setScales([{ isNotScale: true }, { isNotScale: true }]);
        return;
      }

      const newScales = [{ isNotScale: true }];

      segments.slice(0, -1).forEach((currentSegment, index) => {
        const nextSegment = segments[index + 1];
        const waitTime = Math.max(
          (new Date(nextSegment.departure_date_time) - new Date(currentSegment.arrival_date_time)) /
            1000,
          0
        );

        newScales.push({
          id: `scale${index + 1}`,
          origin: `${currentSegment.destination_short_name}, ${currentSegment.destination_city_name}`,
          destination: `${nextSegment.destination_short_name}, ${nextSegment.destination_city_name}`,
          samePlane: false,
          technicalStop: false,
          sameAirport: currentSegment.destination_iata === nextSegment.origin_iata,
          waitTime,
          isNotScale: false,
        });
      });

      newScales.push({ isNotScale: true });
      setScales(newScales);
    };

    calculateScales();
  }, [flight]);

  return (
    <Stack
      className='flight_card__data'
      sx={{
        alignItems: { xs: 'stretch', lg: 'center' },
        flexDirection: { xs: 'column', lg: 'row' },
      }}
    >
      <Stack
        className='flight_card_info_second_row'
        sx={{
          flexDirection: { xs: 'column', lg: 'row' },
          marginTop: { xs: '1rem' },
          pl: 2,
        }}
      >
        {/* Airline Logos */}
        {airlineLogos.length > 0 && (
          <Stack
            sx={{
              alignItems: { xs: 'center', lg: 'flex-start' },
              alignSelf: { xs: 'center', lg: 'auto' },
              flexDirection: { xs: 'row', lg: 'column' },
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
              gap: { xs: 1.5, lg: 0 },
              marginRight: { xs: 0, lg: 2 },
              marginBottom: { xs: 2, lg: 0 },
              paddingLeft: { xs: 1.5, sm: 2, lg: 0 },
              justifyContent: { xs: 'flex-start', lg: 'center' },
              width: { xs: '100%', lg: 'auto' },
            }}
          >
            {airlineLogos.map((logoUrl, index) => (
              <Box
                alt='Airline Logo'
                component='img'
                key={`logo-${index}`}
                src={logoUrl}
                sx={{
                  height: 'auto',
                  marginTop: { xs: 0, lg: index > 0 ? '1rem' : 0 },
                  maxWidth: '100px',
                  width: { xs: 'clamp(78px, 28vw, 112px)', lg: '100%' },
                }}
              />
            ))}
          </Stack>
        )}

        <Stack
          className='flight_card_info'
          sx={{
            flexDirection: 'column',
            paddingLeft: { xs: 2, sm: 3, lg: '1rem' },
            paddingRight: { xs: 2, sm: 3, lg: '1rem' },
          }}
        >
          <Flex className='flight_card_data_jcsb'>
            <Typography fontSize='1rem' fontWeight={700}>
              {formatTime(flightData.departureDate)}
            </Typography>
            <Typography color={coral} fontSize='0.75rem'>
              {!flightData.hasScales
                ? 'Vuelo Directo'
                : `${scales.length - 2} ${scales.length > 3 ? 'escalas' : 'escala'}`}
            </Typography>
            <Typography fontSize='1rem' fontWeight={700}>
              {formatTime(flightData.arrivalDate)}
            </Typography>
          </Flex>

          <Flex
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <div className='dashed-line' />
            <div
              style={{
                display: 'flex',
                flex: 1,
                justifyContent: 'space-between',
                position: 'relative',
              }}
            >
              {scales.map((scale, index) => (
                <TooltipScale escala={scale} isNotScale={scale.isNotScale} key={`scale-${index}`} />
              ))}
            </div>
          </Flex>

          <Flex className='flight_card_data_jcsb'>
            <p className='bottom_label_flight'>{flightData.originId}</p>
            <p className='bottom_label_flight'>{flightData.destinationId}</p>
          </Flex>

          <Flex className='flight_card_data_jcsb' style={{ alignItems: 'flex-start' }}>
            <p className='location_text'>{flightData.departureCity}</p>
            <p className='location_text'>{flightData.arrivalCity}</p>
          </Flex>
        </Stack>
      </Stack>

      <Stack
        sx={{
          alignItems: 'center',
          alignSelf: { xs: 'stretch', lg: 'center' },
          flexDirection: { xs: 'row', lg: 'row' },
          gap: { xs: 1, lg: 0 },
          height: 'auto',
          minHeight: { xs: 40, lg: 'auto' },
          paddingBottom: { xs: 2, lg: 0 },
          paddingTop: { xs: 1, lg: 'inherit' },
          minWidth: { xs: '100%', lg: 'auto' },
          justifyContent: { xs: 'space-between', lg: 'center' },
          paddingLeft: { xs: 2, sm: 3, lg: 0 },
          paddingRight: { xs: 2, sm: 3, lg: 0 },
          textAlign: 'center',
        }}
      >
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'
          sx={{ display: { xs: 'flex', lg: 'none' }, width: '100%' }}
        >
          <Stack className='flight_card_duration_row' direction='row' gap={0.5}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
              Duración:
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300, lineHeight: 1, whiteSpace: 'nowrap' }}>
              {formatSecondsToHours(flightData.duration)}
            </Typography>
          </Stack>

          <Stack alignItems='center' direction='row' gap={1} justifyContent='center'>
            <TooltipLuggage checkedBag={flight.segments[0].baggage} />
            <Stack
              alignItems='center'
              className='flight_card_info_four_row'
              justifyContent='center'
              sx={{ padding: 0 }}
            >
              <FlightSingleDetailDialog
                destinationId={flightData.destinationId}
                duration={flightData.duration}
                flight={flight.segments}
                kind={kind}
                originId={flightData.originId}
                scales={scales}
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack flex={1} gap={1} justifyContent='center' sx={{ display: { xs: 'none', lg: 'flex' }, height: 'auto' }}>
          <TooltipLuggage checkedBag={flight.segments[0].baggage} />
          <Stack flexDirection='column' textAlign='center'>
            <Typography sx={{ fontSize: 14, fontWeight: 300 }}>Duración:</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300 }}>
              {formatSecondsToHours(flightData.duration)}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          sx={{
            alignItems: 'center',
            display: { xs: 'none', lg: 'flex' },
            justifyContent: 'center',
            padding: 2,
          }}
        >
          <FlightSingleDetailDialog
            destinationId={flightData.destinationId}
            duration={flightData.duration}
            flight={flight.segments}
            kind={kind}
            originId={flightData.originId}
            scales={scales}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default FlightSingleInfoRow;
